import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import MuiListItem from '@mui/material/ListItem';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { deepOrange } from '@mui/material/colors';
import FolderRounded from '@mui/icons-material/FolderRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ViewStreamRounded from '@mui/icons-material/ViewStreamRounded';
import { useFormatRelativeDateTime } from 'hooks/useFormatRelativeDateTime';
import { NumberedCheckbox } from '../../NumberedCheckbox';

type LoadedItemProps = {
  startNode: React.ReactNode;
  name: string;
  endNode: React.ReactNode;
  onClick: () => void;
};

type LoadingItemProps = {
  isLoading: boolean;
};

function DialogContent({ children }: { children: React.ReactNode }) {
  return (
    <MuiDialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: 360,
        '& > :not(:last-child)': {
          flexShrink: 0,
        },
        '& > :last-child': {
          flexShrink: 1,
        },
      }}
    >
      {children}
    </MuiDialogContent>
  );
}

function DialogActions({ children }: { children: React.ReactNode }) {
  return <MuiDialogActions sx={{ p: 1.5 }}>{children}</MuiDialogActions>;
}

function ListItem({ startNode, name, endNode, onClick }: LoadedItemProps);
function ListItem({ isLoading }: LoadingItemProps);
function ListItem({ startNode, name, endNode, onClick, isLoading }: Partial<LoadedItemProps & LoadingItemProps>) {
  return (
    <MuiListItem disablePadding dense>
      {isLoading ? (
        <>
          {startNode}
          <ListItemText primary={<Skeleton variant="text" width="100%" height="100%" />} />
          {endNode}
        </>
      ) : (
        <ListItemButton role={undefined} onClick={onClick} dense>
          {startNode}
          <ListItemText primary={name} />
          {endNode}
        </ListItemButton>
      )}
    </MuiListItem>
  );
}

function FolderListItem({ children = '', onClick }: { children: string; onClick: () => void }) {
  return (
    <ListItem
      startNode={
        <FolderRounded
          sx={{
            color: 'action.active',
            mr: 1,
            flexShrink: 0,
          }}
          fontSize="small"
        />
      }
      name={children}
      endNode={
        <ChevronRightRounded
          sx={{
            color: 'action.active',
            ml: 1,
            flexShrink: 0,
            marginLeft: 'auto',
          }}
          fontSize="small"
        />
      }
      onClick={onClick}
    />
  );
}

function SectionListItem({
  content,
  selectedIndex,
  onToggle,
}: {
  content: {
    id: string;
    name: string;
    updatedAt: Date;
  };
  selectedIndex: number;
  onToggle: (id: string) => void;
}) {
  const { formatRelativeDateTime } = useFormatRelativeDateTime();

  const handleToggle = () => {
    onToggle(content.id);
  };

  return (
    <ListItem
      startNode={
        <>
          <NumberedCheckbox
            edge="start"
            tabIndex={-1}
            checked={selectedIndex !== -1}
            label={selectedIndex + 1}
            inputProps={{ 'aria-labelledby': `checkbox-list-label-${content.id}` }}
          />
          <ViewStreamRounded sx={{ mr: 1, fill: deepOrange[500] }} />
        </>
      }
      name={content.name}
      endNode={
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatRelativeDateTime(content.updatedAt.toISOString())}
        </Typography>
      }
      onClick={handleToggle}
    />
  );
}

export const SectionsImportLayout = {
  ListItem,
  FolderListItem,
  SectionListItem,
  DialogContent,
  DialogActions,
};
